import {useCallback, useEffect, useRef, useState} from 'react';
import {useInViewEffect} from '../../../../hooks/useInView/useInViewEffect';

export const useAutoplay = ({
  currentSlide,
  duration,
  isNextActive,
  loop,
  next,
  scrollToSlide,
}: {
  currentSlide: number;
  duration?: number;
  isNextActive: boolean;
  loop: boolean;
  next: VoidFunction;
  scrollToSlide: (position: number) => void;
}) => {
  const sliderTimeoutRef = useRef<NodeJS.Timeout>(undefined);
  const [autoplayStarted, setAutoplayStarted] = useState(false);

  const {ref: inViewRef} = useInViewEffect<HTMLDivElement>(
    inView => {
      inView && startAutoplay();
      setAutoplayStarted(inView);
    },
    {skip: !duration},
  );

  const startAutoplay = useCallback(() => {
    if (!duration) {
      return;
    }

    clearTimeout(sliderTimeoutRef.current);

    sliderTimeoutRef.current = setTimeout(() => {
      if (!isNextActive && loop) {
        scrollToSlide(0);
      } else {
        next();
      }
    }, duration * 1000);

    return () => {
      clearTimeout(sliderTimeoutRef.current);
    };
  }, [duration, isNextActive, loop, next, scrollToSlide]);

  // when the user manually change the slider we want to stop the autoplay
  useEffect(() => {
    clearTimeout(sliderTimeoutRef.current);
  }, [currentSlide]);

  return {inViewRef, autoplayStarted};
};
